// GOLD-12178 / GOLD-12179: Timed Matching Grid (SDMT) helpers — review
// inclusion, sequence scoring, and the GOLD-12199 submit contract.
export const TIMED_MATCHING_GRID_TYPE = 'timed_matching_grid'

// GOLD-12178 / GOLD-12260: TMG is opt-in on Review. Other types default to
// true when in_review is omitted; TMG would otherwise leak a timed sitting
// onto Review whenever authors leave the flag unset.
export const shouldSkipReviewForTimedMatchingGrid = (question = {}) => {
  if ((question?.type || '') !== TIMED_MATCHING_GRID_TYPE) return false
  const inReview =
    question?.attributes?.in_review ??
    question?.attributes?.timed_matching_grid_details?.in_review
  return inReview !== true
}

export const INPUT_TYPES = {
  digit: 'digit',
  checkmark: 'checkmark'
}

export const TIMER_DISPLAY = {
  countdown: 'countdown',
  countup: 'countup'
}

export const DEFAULTS = {
  gridRows: 7,
  gridColumns: 7,
  gridMin: 1,
  gridMax: 20,
  timeLimitSeconds: 90,
  timeLimitSecondsMin: 1,
  timeLimitSecondsMax: 600,
  timerDisplay: TIMER_DISPLAY.countdown,
  randomizeItems: true,
  inputType: INPUT_TYPES.digit
}

// GOLD-12178: Keep authored numbers inside admin min/max so a bad zip value
// cannot create an unusable grid or timer.
const clampInt = (value, min, max, fallback) => {
  const num = Number(value)
  if (!Number.isFinite(num)) return fallback
  return Math.min(max, Math.max(min, Math.round(num)))
}

export const DIGIT_RANGE = [1, 2, 3, 4, 5, 6, 7, 8, 9]

// GOLD-12178: Digit mode only accepts 1–9 (SDMT key). 0 and other keys
// must not write a cell or advance the cursor.
export const isDigitChar = value => /^[1-9]$/.test(String(value ?? ''))

// GOLD-12178: Only treat blob/data/http(s) as images so zip-relative paths
// do not render as broken <img> before media resolution.
export const isRenderableImageSrc = value =>
  typeof value === 'string' &&
  /^(blob:|data:|https?:|\/\/)/i.test(value.trim())

// GOLD-12178: ClinRO/admin send several aliases for the same input mode.
// Normalize so digit vs checkmark logic never depends on exact spelling.
export const normalizeInputType = raw => {
  const value = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
  if (
    value === 'checkmark' ||
    value === 'checkmark_for_incorrect_answers' ||
    value === 'check'
  ) {
    return INPUT_TYPES.checkmark
  }
  return INPUT_TYPES.digit
}

// GOLD-12178: Accept countup / count_up / count-up so the timer label
// matches authoring even when the zip uses mixed naming.
export const normalizeTimerDisplay = raw => {
  const value = String(raw || '')
    .trim()
    .toLowerCase()
  if (value === 'countup' || value === 'count_up' || value === 'count-up') {
    return TIMER_DISPLAY.countup
  }
  return TIMER_DISPLAY.countdown
}

// GOLD-12178: Symbol key can arrive as an array, object map, or primitive
// list depending on authoring version. Flatten to one {digit, character,
// image_url} shape so the key row and cell lookup share the same data.
export const normalizeSymbolKey = symbolKey => {
  if (!symbolKey) return []
  if (Array.isArray(symbolKey)) {
    return symbolKey
      .map((entry, idx) => {
        if (entry == null) return null
        if (typeof entry === 'string' || typeof entry === 'number') {
          return {
            digit: idx + 1,
            character: String(entry)
          }
        }
        const digit = Number(entry.digit ?? entry.key ?? entry.value ?? idx + 1)
        if (!Number.isFinite(digit) || digit < 1 || digit > 9) return null
        return {
          digit,
          character:
            entry.character != null
              ? String(entry.character)
              : entry.char != null
                ? String(entry.char)
                : entry.label != null
                  ? String(entry.label)
                  : undefined,
          image_url:
            entry.image_url ||
            entry.imageUrl ||
            entry.image ||
            entry.imageSrc ||
            entry.url ||
            entry.src ||
            undefined
        }
      })
      .filter(Boolean)
      .sort((a, b) => a.digit - b.digit)
  }

  if (typeof symbolKey === 'object') {
    return Object.keys(symbolKey)
      .map(key => {
        const entry = symbolKey[key]
        const digit = Number(entry?.digit ?? key)
        if (!Number.isFinite(digit) || digit < 1 || digit > 9) return null
        if (typeof entry === 'string') {
          return { digit, character: entry }
        }
        return {
          digit,
          character:
            entry?.character != null
              ? String(entry.character)
              : entry?.char != null
                ? String(entry.char)
                : undefined,
          image_url:
            entry?.image_url ||
            entry?.imageUrl ||
            entry?.image ||
            entry?.imageSrc ||
            entry?.url ||
            entry?.src ||
            undefined
        }
      })
      .filter(Boolean)
      .sort((a, b) => a.digit - b.digit)
  }

  return []
}

// GOLD-12178: item_sequence entries may be a digit or an object. Keep
// optional prefill/response so checkmark cells can show the displayed number.
const normalizeSequenceItem = (item, index) => {
  if (item == null) return null
  if (typeof item === 'number' || typeof item === 'string') {
    const symbolDigit = Number(item)
    if (!Number.isFinite(symbolDigit)) return null
    return {
      index,
      symbol_digit: symbolDigit
    }
  }
  const symbolDigit = Number(
    item.symbol_digit ?? item.symbolDigit ?? item.digit ?? item.value
  )
  if (!Number.isFinite(symbolDigit)) return null
  const responseRaw =
    item.response ?? item.displayed_digit ?? item.displayedDigit ?? item.prefill
  const response =
    responseRaw === undefined || responseRaw === null || responseRaw === ''
      ? undefined
      : Number(responseRaw)
  return {
    index: Number.isFinite(Number(item.index)) ? Number(item.index) : index,
    symbol_digit: symbolDigit,
    ...(Number.isFinite(response) ? { response } : {})
  }
}

// GOLD-12178: Fisher–Yates used only when the authored sequence is missing
// or shorter than the grid. A complete item_sequence is never shuffled.
export const shuffleArray = (items, randomFn = Math.random) => {
  const next = [...items]
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(randomFn() * (i + 1))
    ;[next[i], next[j]] = [next[j], next[i]]
  }
  return next
}

// GOLD-12178: When authoring omits item_sequence, repeat the key digits so
// every cell still has a symbol and the grid is not left empty.
export const buildBalancedSequence = (totalCells, keyDigits = DIGIT_RANGE) => {
  const digits = keyDigits.length ? keyDigits : DIGIT_RANGE
  const sequence = []
  while (sequence.length < totalCells) {
    sequence.push(...digits)
  }
  return sequence.slice(0, totalCells).map((symbolDigit, index) => ({
    index,
    symbol_digit: Number(symbolDigit)
  }))
}

// GOLD-12178: Preserve administration order from the zip when it fills the
// grid. Shuffle only a missing/short sequence so randomizeItems: true
// (the default) cannot reorder a complete authored sitting.
export const resolveItemSequence = ({
  itemSequence,
  gridRows,
  gridColumns,
  randomizeItems,
  symbolKey,
  randomFn = Math.random
}) => {
  const totalCells = Math.max(1, gridRows * gridColumns)
  const keyDigits = (symbolKey || [])
    .map(entry => Number(entry.digit))
    .filter(digit => Number.isFinite(digit) && digit >= 1 && digit <= 9)

  const provided = Array.isArray(itemSequence)
    ? itemSequence
        .map((item, index) => normalizeSequenceItem(item, index))
        .filter(Boolean)
        .slice(0, totalCells)
    : []

  if (provided.length >= totalCells) {
    return provided.slice(0, totalCells).map((item, index) => ({
      ...item,
      index
    }))
  }

  if (provided.length > 0) {
    const filler = buildBalancedSequence(
      totalCells - provided.length,
      keyDigits
    )
    const combined = [...provided, ...filler].slice(0, totalCells)
    const ordered = randomizeItems ? shuffleArray(combined, randomFn) : combined
    return ordered.map((item, index) => ({
      ...item,
      index
    }))
  }

  const generated = buildBalancedSequence(totalCells, keyDigits)
  const ordered = randomizeItems ? shuffleArray(generated, randomFn) : generated
  return ordered.map((item, index) => ({
    ...item,
    index
  }))
}

// GOLD-12178: Clamp and alias-normalize zip attributes so the reducer
// always starts from a complete config even if some fields are omitted.
export const parseTimedMatchingGridDetails = (attributes = {}) => {
  const details =
    attributes.timed_matching_grid_details ||
    attributes.timedMatchingGridDetails ||
    {}

  const gridRows = clampInt(
    details.grid_rows ?? details.gridRows,
    DEFAULTS.gridMin,
    DEFAULTS.gridMax,
    DEFAULTS.gridRows
  )
  const gridColumns = clampInt(
    details.grid_columns ?? details.gridColumns,
    DEFAULTS.gridMin,
    DEFAULTS.gridMax,
    DEFAULTS.gridColumns
  )
  const timeLimitSeconds = clampInt(
    details.time_limit_seconds ?? details.timeLimitSeconds,
    DEFAULTS.timeLimitSecondsMin,
    DEFAULTS.timeLimitSecondsMax,
    DEFAULTS.timeLimitSeconds
  )
  const timerDisplay = normalizeTimerDisplay(
    details.timer_display ?? details.timerDisplay
  )
  const randomizeItems = Boolean(
    details.randomize_items ??
      details.randomizeItems ??
      DEFAULTS.randomizeItems
  )
  const inputType = normalizeInputType(
    details.input_type ?? details.inputType ?? DEFAULTS.inputType
  )
  const symbolKey = normalizeSymbolKey(
    details.symbol_key ?? details.symbolKey ?? details.key
  )

  const itemSequence = resolveItemSequence({
    itemSequence: details.item_sequence ?? details.itemSequence,
    gridRows,
    gridColumns,
    randomizeItems,
    symbolKey
  })

  return {
    gridRows,
    gridColumns,
    timeLimitSeconds,
    timerDisplay,
    randomizeItems,
    inputType,
    symbolKey,
    itemSequence
  }
}

// GOLD-12178: Checkmark cells start with the displayed digit prefilled
// because the participant marks incorrect items rather than typing answers.
export const createCellsFromSequence = (itemSequence, inputType) =>
  (itemSequence || []).map((item, index) => {
    const cell = {
      index: Number.isFinite(Number(item.index)) ? Number(item.index) : index,
      symbol_digit: Number(item.symbol_digit),
      attempted: false,
      ticked: false,
      reviewed: false
    }
    if (inputType === INPUT_TYPES.checkmark) {
      const prefilled =
        item.response ?? item.displayed_digit ?? item.displayedDigit
      if (prefilled !== undefined && prefilled !== null && prefilled !== '') {
        cell.response = Number(prefilled)
      } else {
        cell.response = Number(item.symbol_digit)
      }
    } else if (
      item.response !== undefined &&
      item.response !== null &&
      item.response !== ''
    ) {
      cell.response = Number(item.response)
      cell.attempted = true
      cell.reviewed = true
    }
    return cell
  })

// GOLD-12178: After a digit entry, jump to the next empty cell (wrapping)
// so the participant does not have to tab through already-filled cells.
export const findNextEmptyIndex = (cells, fromIndex) => {
  if (!cells?.length) return fromIndex
  const start = Math.max(0, fromIndex)
  for (let i = start; i < cells.length; i += 1) {
    const response = cells[i]?.response
    if (response === undefined || response === null || response === '') {
      return i
    }
  }
  for (let i = 0; i < start; i += 1) {
    const response = cells[i]?.response
    if (response === undefined || response === null || response === '') {
      return i
    }
  }
  return Math.min(start, cells.length - 1)
}

// GOLD-12178: Digit progress is "cells that have a number", not attempted
// flags, so a cleared cell no longer counts as done.
export const countAnswered = cells =>
  (cells || []).filter(cell => {
    const response = cell?.response
    return response !== undefined && response !== null && response !== ''
  }).length

// GOLD-12178: Checkmark counter shows how many items were marked wrong.
export const countTicked = cells =>
  (cells || []).filter(cell => !!cell?.ticked).length

// GOLD-12178: Inclusive count from index 0 through the farthest cursor cell.
// Kept for cursor helpers; checkmark review now counts visited cells, not
// this prefix (jumping to the last cell used to mark skipped cells done).
export const reviewedCountFromCursor = cursorMaxIndex => {
  if (!Number.isFinite(cursorMaxIndex) || cursorMaxIndex < 0) return 0
  return cursorMaxIndex + 1
}

// GOLD-12178: High-water cursor for restore/focus. Not used for scoring —
// a later focus must not treat skipped cells as reviewed.
export const lastTrackedCellIndex = (state = {}) => {
  const cursorMax = Number(state.cursorMaxIndex)
  const focus = Number(state.focusIndex)
  return Math.max(
    Number.isFinite(cursorMax) ? cursorMax : -1,
    Number.isFinite(focus) ? focus : -1
  )
}

// A cell is reviewed only when the cursor has actually landed on it.
export const isCellReviewed = cell => !!cell?.reviewed

export const markCellsReviewed = (cells, ...indices) => {
  const wanted = new Set(
    indices.map(index => Number(index)).filter(Number.isFinite)
  )
  if (!wanted.size || !cells?.length) return cells
  return cells.map(cell =>
    wanted.has(cell.index) ? { ...cell, reviewed: true } : cell
  )
}

// Checkmark progress is cells the cursor visited, not a contiguous prefix.
export const countItemsSupervised = (state = {}) =>
  (state.cells || []).filter(isCellReviewed).length

// GOLD-12178: Digit is complete only when every cell has a number.
// Checkmark is complete only when every cell has been focused.
export const isTimedMatchingGridFullyAnswered = (state = {}) => {
  const cells = state.cells || []
  if (!cells.length) return false
  if ((state.inputType || INPUT_TYPES.digit) === INPUT_TYPES.checkmark) {
    return countItemsSupervised(state) >= cells.length
  }
  return countAnswered(cells) >= cells.length
}

// GOLD-12178: Next unlocks when the sitting is complete OR the timer
// ended, so participants can leave a partial grid after expire without
// treating early Next as timed_out.
export const canEnableTimedMatchingGridNext = (state = {}) =>
  !!state.expired || !!state.locked || isTimedMatchingGridFullyAnswered(state)

// GOLD-12178: countup shows elapsed, countdown shows remaining. Both stop
// at the authored limit so the header never goes negative or past 90s.
export const formatTimerDisplay = (elapsedSeconds, timeLimitSeconds, mode) => {
  const safeElapsed = Math.max(0, Number(elapsedSeconds) || 0)
  const safeLimit = Math.max(1, Number(timeLimitSeconds) || 1)
  const remaining = Math.max(0, safeLimit - safeElapsed)
  const seconds =
    mode === TIMER_DISPLAY.countup
      ? Math.min(safeElapsed, safeLimit)
      : remaining
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${String(secs).padStart(2, '0')}`
}

// GOLD-12199 / GOLD-12181: -1 means skipped/unreached. Sequence length must
// always match the full grid so scoring can align index-to-index.
export const UNREACHED_SENTINEL = -1

// GOLD-12199: 1 = reached and left unchecked (correct), 0 = ticked wrong.
export const CHECKMARK_MARK = {
  correct: 1,
  incorrect: 0
}

// GOLD-12199: Empty/undefined digit cells become -1, not 0, so scoring
// does not treat an unanswered cell as a typed zero.
const hasDigitResponse = cell => {
  const response = cell?.response
  return response !== undefined && response !== null && response !== ''
}

// GOLD-12199: Keep one submitted value per cell so the server can score
// a partial sitting without dropping trailing unanswered items.
const buildDigitSubmittedSequence = cells =>
  (cells || []).map(cell =>
    hasDigitResponse(cell) ? Number(cell.response) : UNREACHED_SENTINEL
  )

// GOLD-12199 / GOLD-12181: Submit the sequence contract the server scores,
// not UI cell objects. Digit: 1–9 or -1. Checkmark: 1 = cursor visited and
// not ticked, 0 = ticked wrong, -1 = never focused. Early Next is not
// timed_out; only the timer sets timed_out.
export const buildAnswerPayload = state => {
  const cells = state.cells || []
  const inputType = state.inputType || INPUT_TYPES.digit
  const correctItemSequence = cells.map(cell => Number(cell.symbol_digit))

  const submittedItemSequence =
    inputType === INPUT_TYPES.checkmark
      ? cells.map(cell => {
          if (!isCellReviewed(cell)) return UNREACHED_SENTINEL
          return cell.ticked
            ? CHECKMARK_MARK.incorrect
            : CHECKMARK_MARK.correct
        })
      : buildDigitSubmittedSequence(cells)

  return {
    input_type: inputType,
    correct_item_sequence: correctItemSequence,
    submitted_item_sequence: submittedItemSequence,
    elapsed_seconds: Number(state.elapsedSeconds) || 0,
    timed_out: !!state.timedOut
  }
}

export const TMG_SUBMIT_ANSWER_KEYS = [
  'input_type',
  'correct_item_sequence',
  'submitted_item_sequence',
  'elapsed_seconds',
  'timed_out'
]

// GOLD-12199: Drop UI-only fields (cells, focus, ticked) so the final
// POST matches the scoring contract and nothing extra is persisted.
export const pickTimedMatchingGridSubmitAnswer = (answer = {}) => {
  const source =
    answer?.value && typeof answer.value === 'object'
      ? { ...answer, ...answer.value }
      : answer
  return TMG_SUBMIT_ANSWER_KEYS.reduce((acc, key) => {
    if (source?.[key] !== undefined) acc[key] = source[key]
    return acc
  }, {})
}

// GOLD-12178: Rebuild cursorMaxIndex from a stored sequence so Back /
// restore lands on the last reached cell, not cell 0.
export const lastReachedIndexFromSubmitted = submitted => {
  if (!Array.isArray(submitted)) return -1
  let last = -1
  submitted.forEach((value, index) => {
    if (value !== UNREACHED_SENTINEL) last = index
  })
  return last
}

// GOLD-12178: Restore from either in-progress cells or the submitted
// sequence so Back/refresh can rebuild the same grid after Next.
export const restoreCellsFromAnswer = (config, restored) => {
  if (Array.isArray(restored?.cells)) {
    return config.itemSequence.map((item, index) => {
      const match =
        restored.cells.find(cell => Number(cell.index) === index) ||
        restored.cells[index] ||
        {}
      return {
        index,
        symbol_digit: Number(
          match.symbol_digit ?? item.symbol_digit ?? item.digit
        ),
        response:
          match.response !== undefined && match.response !== null
            ? Number(match.response)
            : config.inputType === INPUT_TYPES.checkmark
              ? Number(
                  item.response ?? item.displayed_digit ?? item.symbol_digit
                )
              : undefined,
        attempted: !!match.attempted,
        ticked: !!match.ticked,
        reviewed:
          match.reviewed !== undefined
            ? !!match.reviewed
            : !!match.attempted || !!match.ticked
      }
    })
  }

  const submitted = restored?.submitted_item_sequence
  const correct = restored?.correct_item_sequence
  return config.itemSequence.map((item, index) => {
    const submittedValue = Array.isArray(submitted) ? submitted[index] : undefined
    const symbolDigit = Number(
      Array.isArray(correct) && correct[index] != null
        ? correct[index]
        : item.symbol_digit ?? item.digit
    )
    if (config.inputType === INPUT_TYPES.checkmark) {
      return {
        index,
        symbol_digit: symbolDigit,
        response: Number(
          item.response ?? item.displayed_digit ?? item.symbol_digit
        ),
        attempted:
          submittedValue !== undefined && submittedValue !== UNREACHED_SENTINEL,
        ticked: submittedValue === 0,
        reviewed:
          submittedValue !== undefined && submittedValue !== UNREACHED_SENTINEL
      }
    }
    return {
      index,
      symbol_digit: symbolDigit,
      response:
        submittedValue !== undefined && submittedValue !== UNREACHED_SENTINEL
          ? Number(submittedValue)
          : undefined,
      attempted:
        submittedValue !== undefined && submittedValue !== UNREACHED_SENTINEL,
      ticked: false,
      reviewed:
        submittedValue !== undefined && submittedValue !== UNREACHED_SENTINEL
    }
  })
}

// GOLD-12178: Answers may be a JSON string or an object depending on
// how localStorage vs live submission stored them.
export const parseStoredAnswer = storedAnswer => {
  const raw = storedAnswer?.[0]?.value ?? storedAnswer?.[0]
  if (!raw) return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  }
  if (typeof raw === 'object') return raw
  return null
}

// GOLD-12178: Treat a sitting as finished only after Next persisted
// submitted_item_sequence. Timer expire is not autosaved, so refresh
// before Next must start a new sitting instead of locking an empty grid.
export const isTimedMatchingGridSittingComplete = storedAnswer => {
  const parsed = parseStoredAnswer(storedAnswer)
  return Array.isArray(parsed?.submitted_item_sequence)
}

// GOLD-12178 / GOLD-12409: Questions stay mounted; Next/Back only
// scrolls. When TMG is not the active screen: restart an incomplete
// sitting so the timer does not keep running on Select One, or lock a
// finished grid. Timer expire is also finished (Next is enabled), so
// Back must lock instead of restarting and clearing answers.
export const getTimedMatchingGridLeaveAction = ({
  isActiveQuestion,
  isSittingLocked,
  isGridComplete,
  isExpired
} = {}) => {
  if (isActiveQuestion) return null
  if (isSittingLocked || isGridComplete || isExpired) return 'lock'
  return 'restart'
}

// GOLD-12178: If a later question is already in the session, this TMG
// page was completed. Lock it so Back cannot start a second timed sitting.
export const hasAdvancedPastTimedMatchingGrid = (
  questionId,
  currentSurvey = []
) => {
  if (!questionId || !Array.isArray(currentSurvey)) return false
  const idx = currentSurvey.indexOf(questionId)
  if (idx === -1) return false
  return currentSurvey.slice(idx + 1).some(id => !!id)
}

// GOLD-12178: Prefer the live submission answer over props.storedAnswer
// so Next → Back sees the just-persisted sequence, not a stale copy.
export const getTimedMatchingGridStoredAnswer = ({
  questionId,
  submissionSurvey,
  storedAnswer
} = {}) => {
  const live = (submissionSurvey?.questions || []).find(
    question => question?.id === questionId
  )?.answer
  return live || storedAnswer
}
